import { configStore, globalStore } from "@/store/store";
import {
  Control,
  InheritancePosition,
  OxygenInheritance,
  OxygenInheritanceValue,
  OxySelector
} from "@/oxygen/classes/lib/types";
import { pipe } from "fp-ts/function";
import { GradientColorV } from "@/types/colors";
import { fold } from "fp-ts/Either";

function isValueEmpty(value: unknown) {
  return value === "" || value === null || value === undefined;
}

function isUnitValue(
  obj: unknown
): obj is { unit: string; number: number; style: string } {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "unit" in obj &&
    "number" in obj &&
    "style" in obj
  );
}

function isNestedObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !isUnitValue(value);
}

function getPosition(
  currentIndex: number,
  loopedIndex: number
): InheritancePosition {
  if (loopedIndex > currentIndex) return InheritancePosition.AFTER;
  if (loopedIndex === currentIndex) return InheritancePosition.CURRENT;
  return InheritancePosition.BEFORE;
}

function processNestedProperties(
  property: string,
  value: Record<string, unknown>,
  selectorId: string,
  inheritanceMap: OxygenInheritance,
  position: OxygenInheritanceValue["position"],
  breakpoint: string,
  currentBreakpointIndex: number,
  breakpointIndex: number
) {
  for (const subProperty in value) {
    const fullProperty = `${property}.${subProperty}`;
    const subValue = value[subProperty];

    if (isNestedObject(subValue)) {
      for (const deepProperty in subValue) {
        const deepFullProperty = Array.isArray(subValue)
          ? `${fullProperty}[${deepProperty}]`
          : `${fullProperty}.${deepProperty}`;

        const deepValue = subValue[deepProperty];

        if (isNestedObject(deepValue)) {
          processNestedProperties(
            deepFullProperty,
            deepValue,
            selectorId,
            inheritanceMap,
            position,
            breakpoint,
            currentBreakpointIndex,
            breakpointIndex
          );
        } else if (!isValueEmpty(deepValue)) {
          inheritanceMap[deepFullProperty] = {
            value: deepValue,
            position,
            breakpoint,
            from: selectorId,
            fromBreakpoint: breakpointIndex < currentBreakpointIndex
          };
        }
      }
    } else if (!isValueEmpty(subValue)) {
      inheritanceMap[fullProperty] = {
        value: subValue,
        position,
        breakpoint,
        from: selectorId,
        fromBreakpoint: breakpointIndex < currentBreakpointIndex
      };
    }
  }
}

function processBreakpointProperties(
  breakpointProps: Record<string, unknown>,
  selectorId: string,
  inheritanceMap: OxygenInheritance,
  position: OxygenInheritanceValue["position"],
  breakpoint: string,
  currentBreakpointIndex: number,
  breakpointIndex: number
) {
  for (const property in breakpointProps) {
    const value = breakpointProps[property];

    if (isNestedObject(value)) {
      processNestedProperties(
        property,
        value,
        selectorId,
        inheritanceMap,
        position,
        breakpoint,
        currentBreakpointIndex,
        breakpointIndex
      );
    } else if (!isValueEmpty(value)) {
      inheritanceMap[property] = {
        value,
        position,
        breakpoint,
        from: selectorId,
        fromBreakpoint: breakpointIndex < currentBreakpointIndex
      };
    }
  }
}
export function getInheritanceMap(
  elementSelectorsIds: string[], // unordered, does not follow css cascade order
  currentSelectorId: string,
  currentBreakpoint: string
): OxygenInheritance {
  const inheritanceMap = {};
  const allSelectors = globalStore.oxySelectors.flatMap(s => [
    s,
    ...(s.children || [])
  ]);

  const currentSelectors = elementSelectorsIds
    .map(id => {
      const selector = allSelectors.find(s => s.id === id);
      if (!selector) return null;
      return selector;
    })
    .filter(s => s !== null) as OxySelector[];

  // Sort currentSelectors to respect the order from allSelectors
  const sortedCurrentSelectors = [...currentSelectors].sort((a, b) => {
    const aIndex = allSelectors.findIndex(s => s.id === a.id);
    const bIndex = allSelectors.findIndex(s => s.id === b.id);
    return aIndex - bIndex;
  });

  const currentSelectorIndex = sortedCurrentSelectors.findIndex(
    s => s.id === currentSelectorId
  );
  const currentBreakpointIndex = configStore.breakpointIds.findIndex(
    b => b === currentBreakpoint
  );
  const breakpointsUpToCurrent = configStore.breakpointIds.slice(
    0,
    currentBreakpointIndex + 1
  );

  for (let i = 0; i < sortedCurrentSelectors.length; i++) {
    const selector = sortedCurrentSelectors[i];
    const position = getPosition(currentSelectorIndex, i);

    for (let j = 0; j < breakpointsUpToCurrent.length; j++) {
      const breakpoint = breakpointsUpToCurrent[j];
      const props = selector.properties?.[breakpoint];

      if (props) {
        processBreakpointProperties(
          props,
          selector.id,
          inheritanceMap,
          position,
          breakpoint,
          currentBreakpointIndex,
          j
        );
      }
    }
  }

  return inheritanceMap;
}

export function filterKeysByPrefix(
  obj: OxygenInheritance | undefined,
  prefix: string
) {
  if (!obj) return undefined;

  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (key.startsWith(prefix)) {
      acc[key.slice(prefix.length)] = value;
    }
    return acc;
  }, {} as OxygenInheritance);
}

export function controlValueToPlaceholder(
  control: Control,
  value: unknown
): unknown {
  if (control.type === "select") {
    const foundItem = control.items?.find(function (item) {
      if (typeof item === "string") {
        return item === value;
      }
      return item.value === value;
    });

    if (typeof foundItem === "string") {
      return foundItem;
    }

    return foundItem?.label;
  }

  if (control.type === "unit") {
    return value;
  }

  if (control.type === "font-family") {
    const foundVariable = configStore.fonts.find(font => font.slug === value);
    return foundVariable?.label || "";
  }

  if (control.type === "color") {
    return pipe(
      value,
      GradientColorV.decode,
      fold(
        () => value,
        gradient => gradient.type
      )
    );
  }

  if (control.type === "spacing") {
    return value as Record<string, unknown>;
  }

  if (typeof value === "string") {
    return value;
  }

  return "";
}

export function shouldShowInheritanceAsPlaceholder(
  inheritanceValue?: OxygenInheritanceValue
): inheritanceValue is OxygenInheritanceValue {
  if (
    inheritanceValue?.value === null ||
    inheritanceValue?.value === undefined
  ) {
    return false;
  }
  return isValueInheritted(inheritanceValue);
}

export function isValueOverwritten(
  inheritanceValue?: OxygenInheritanceValue
): inheritanceValue is OxygenInheritanceValue {
  return inheritanceValue?.position === InheritancePosition.AFTER;
}

export function isValueInheritted(
  inheritanceValue?: OxygenInheritanceValue
): inheritanceValue is OxygenInheritanceValue {
  const isBefore = inheritanceValue?.position === InheritancePosition.BEFORE;
  return isBefore || isValueFromBreakpoint(inheritanceValue);
}

export function isValueFromBreakpoint(
  inheritanceValue?: OxygenInheritanceValue
): inheritanceValue is OxygenInheritanceValue {
  return (
    !!inheritanceValue?.fromBreakpoint &&
    inheritanceValue?.position === InheritancePosition.CURRENT
  );
}
